import type { FC, ReactNode } from 'react'
import type { Memory } from '@/types'

import Link from 'next/link'
import { Quote } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MemoryCardProps {
    memory: Memory
    showPerson?: boolean
    className?: string
}

const MemoryCard: FC<MemoryCardProps> = ({ memory, showPerson = true, className }): ReactNode => {
    return (
        <figure className={cn('flex h-full flex-col rounded-2xl border border-line bg-linen p-6 shadow-soft md:p-7', className)}>
            <Quote className='size-6 text-gold' aria-hidden='true' />

            <blockquote className='mt-4 flex-1'>
                <p
                    data-mdlfy-cms={`contributions:${memory.id}:message`}
                    className='font-heading text-lg leading-relaxed whitespace-pre-line text-ink'
                >
                    {memory.message}
                </p>
            </blockquote>

            <figcaption className='mt-6 border-t border-line pt-4 text-sm'>
                <span data-mdlfy-cms={`contributions:${memory.id}:name`} className='block font-semibold text-ink'>
                    {memory.name}
                </span>

                {memory.relation && (
                    <span data-mdlfy-cms={`contributions:${memory.id}:relation`} className='block text-umber'>
                        {memory.relation}
                    </span>
                )}

                <span className='mt-2 flex flex-wrap gap-x-3 gap-y-1 text-umber'>
                    {memory.shared_on && (
                        <span>
                            Shared {memory.shared_on}
                        </span>
                    )}

                    {showPerson && memory.person && (
                        <span>
                            About{' '}
                            <Link href={`/people/${memory.person.slug}`} className='font-semibold text-forest underline-offset-4 hover:underline'>
                                <span data-mdlfy-cms={`people:${memory.person.id}:name`}>
                                    {memory.person.name}
                                </span>
                            </Link>
                        </span>
                    )}
                </span>
            </figcaption>
        </figure>
    )
}

export default MemoryCard
